import { useState, type MouseEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRightLeft, AtSign, CopyPlus, DatabaseBackup, History } from "lucide-react";
import { getPlayerDataSnapshot, getPlayerIdentities, lookupUser } from "@/lib/api";
import { useDirtyStore } from "@/lib/dirty-store";
import { PlayerInspector, type InspectorTab } from "@/components/players/PlayerInspector";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import { MigrateLegacyDialog } from "@/components/player-actions/MigrateLegacyDialog";
import { MigrationEmailDialog } from "@/components/player-actions/MigrationEmailDialog";
import { MoveLoginDialog } from "@/components/player-actions/MoveLoginDialog";
import { CopyDevAccountDialog } from "@/components/player-actions/CopyDevAccountDialog";
import "./admin.css";

const TABS = new Set(["account", "heroes", "inventory", "costumes", "purchases", "mail", "flags", "raw", "rollback"]);
type Action = "migrate" | "migration-email" | "move-google" | "copy-dev";

/** Closes the Actions menu the clicked item sits in. */
const closeMenu = (event: MouseEvent<HTMLElement>) => event.currentTarget.closest("details")?.removeAttribute("open");

export function PlayFabAccountWorkspace() {
  const { playfabId } = useParams<{ playfabId: string }>();
  return <PlayFabAccount key={playfabId} id={playfabId!} />;
}

/** One PlayFab account in the workspace shell: the player inspector plus the legacy-only account actions. */
function PlayFabAccount({ id }: { id: string }) {
  const readOnly = usePreviewReadOnly();
  const nav = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.has(params.get("tab") ?? "") ? params.get("tab") : "account") as InspectorTab;
  const [action, setAction] = useState<Action | null>(null);
  const [notice, setNotice] = useState("");
  // Same query keys as the inspector, so these share its requests.
  const account = useQuery({ queryKey: ["pf-account", id], queryFn: () => lookupUser("playfabId", id) });
  const save = useQuery({ queryKey: ["pf-userdata", id], queryFn: () => getPlayerDataSnapshot(id) });
  const identities = useQuery({ queryKey: ["player-identities", id], queryFn: () => getPlayerIdentities(id) });

  const profile = account.data;
  const name = profile?.TitleInfo?.DisplayName || profile?.Username || "Unnamed player";
  const target = { id, displayName: profile?.TitleInfo?.DisplayName || profile?.Username || null };
  const backend = save.data?.source === "cloudflare";
  const unmigrated = save.data?.source === "playfab" && !/^EC2X/i.test(id);

  function change(patch: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => { if (value == null) next.delete(key); else next.set(key, value); });
    setParams(next, { replace: true });
  }
  // Back keeps the search that led here; a direct visit falls back to the legacy search.
  function back() {
    if (!useDirtyStore.getState().confirmLeave()) return;
    if (location.key !== "default") nav(-1); else nav("/players");
  }
  function open(next: Action) { setNotice(""); setAction(next); }
  const blocked = readOnly ? "Read-only preview" : undefined;
  function item(key: Action, icon: ReactNode, label: string, disabled?: string) {
    return <button key={key} type="button" disabled={!!(blocked ?? disabled)} title={blocked ?? disabled} onClick={event => { closeMenu(event); open(key); }}>{icon}{label}</button>;
  }

  const actions = <>
    {unmigrated && item("migrate", <DatabaseBackup size={15} />, "Migrate legacy account")}
    {item("copy-dev", <CopyPlus size={15} />, "Copy as dev account", backend ? undefined : "Migrate this account before making a dev copy")}
    {backend && item("migration-email", <AtSign size={15} />, "Set linked email and password")}
    {backend && item("move-google", <ArrowRightLeft size={15} />, "Move Google login")}
    <Link to={`/audit?playfabId=${encodeURIComponent(id)}`} onClick={event => { if (!useDirtyStore.getState().confirmLeave()) event.preventDefault(); }}><History size={15} />Audit history</Link>
  </>;

  return <WorkspaceShell className="pw-has-player" crumbs={[{ label: "Administration" }, { label: "PlayFab (legacy)", to: "/players" }, { label: account.isPending ? id : name }]} readOnly={readOnly}>
    {unmigrated && <div className="ws-alert is-warn ad-legacy-banner"><span>Not migrated. This account still reads from PlayFab, so its game data can't be edited here.</span>
      <button className="pw-button" disabled={!!blocked} title={blocked} onClick={() => open("migrate")}><DatabaseBackup size={14} />Migrate</button></div>}
    {notice && <p className="ws-alert is-ok ad-legacy-banner" role="status">{notice}</p>}
    <div className="pw-content">
      <PlayerInspector id={id} tab={tab} onTabChange={next => change({ tab: next === "account" ? null : next })} onClose={back} closeLabel="Back"
        readOnly={readOnly} actions={actions} onAccountDeleted={() => nav("/players", { replace: true })} requestedAction={params.get("action")} onActionClose={() => change({ action: null })} />
    </div>

    {action === "migrate" && <MigrateLegacyDialog target={target} onClose={() => setAction(null)} onMigrated={message => { setNotice(message); setAction(null); }} />}
    {action === "migration-email" && <MigrationEmailDialog target={target} onClose={() => setAction(null)}
      initialEmail={identities.data?.find(i => i.provider === "password")?.subject ?? identities.data?.find(i => i.provider === "playfab")?.email ?? ""} />}
    {action === "move-google" && <MoveLoginDialog provider="google" target={target} onClose={() => setAction(null)} onMoved={message => { setNotice(message); setAction(null); }} />}
    {action === "copy-dev" && <CopyDevAccountDialog target={target} saveVersion={save.data?.saveVersion ?? 0} onClose={() => setAction(null)} />}
  </WorkspaceShell>;
}
